'use client';

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider as DefaultMsalProvider } from '@azure/msal-react';
import { useEffect, useState, useMemo } from 'react';
import LoadingFallback from './LoadingFallback';
import { getMsalConfig } from '@/config/authConfig';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useAuthStore, POST_MSAL_LOGIN_RETURN_PATH_KEY } from '@/stores/authStore';
import { userHasEppn } from '@/lib/auth/sessionAuth';
import { scopedSessionStorage } from '@/lib/browser/scopedStorage';
import { log } from '@/lib/utils/logger';

/**
 * MSAL (Microsoft Authentication Library) Provider component.
 * Handles SSR-safe initialization of MSAL and provides authentication context.
 *
 * This component is responsible ONLY for:
 * - Initializing MSAL on client-side
 * - Providing authentication context to child components
 * - Managing authentication state in the auth store
 *
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to be wrapped by the MSAL provider
 * @returns {JSX.Element} Children wrapped with MSAL authentication context
 */
export default function MsalProvider({ children }: { children: React.ReactNode }) {
  const [msalReady, setMsalReady] = useState(false);
  const systemConfig = useSystemConfig();
  const { setMsalInstance, setIsClient, ensureAppAuthReady, isInitialized: appAuthReady } = useAuthStore();
  const isClient = typeof window !== 'undefined';

  // Don't try to create MSAL instance until we have config
  const msalInstance = useMemo(() => {
    if (!isClient || !systemConfig) {
      return null;
    }
    try {
      return new PublicClientApplication(getMsalConfig(systemConfig));
    } catch (error) {
      log.error('[MsalProvider] Failed to create MSAL instance:', error);
      return null;
    }
  }, [isClient, systemConfig]);

  useEffect(() => {
    if (!isClient) return;

    setIsClient(true); // Mark as client-side

    if (msalInstance) {
      msalInstance.initialize().then(async () => {
        setMsalInstance(msalInstance); // Store MSAL instance in auth store

        let shouldMarkInitialized = true;
        try {
          const result = await msalInstance.handleRedirectPromise({
            navigateToLoginRequestUrl: false,
          });
          if (result?.account) {
            useAuthStore.getState().clearViewingPublicSession();
          }
          await ensureAppAuthReady();

          if (result?.account) {
            const appAuthOk = userHasEppn(useAuthStore.getState().currentUser);

            // Only consume the stored path after a completed redirect login (avoids stale navigations).
            try {
              const returnPath = scopedSessionStorage.getItem(POST_MSAL_LOGIN_RETURN_PATH_KEY);
              if (returnPath) {
                scopedSessionStorage.removeItem(POST_MSAL_LOGIN_RETURN_PATH_KEY);
              }
              if (appAuthOk && returnPath) {
                const nextUrl = returnPath.startsWith('http')
                  ? returnPath
                  : `${window.location.origin}${returnPath.startsWith('/') ? returnPath : `/${returnPath}`}`;
                const current = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
                if (nextUrl.replace(/\/$/, '') !== current.replace(/\/$/, '')) {
                  window.location.replace(nextUrl);
                  shouldMarkInitialized = false;
                }
              }
            } catch (storageError) {
              log.warn('[MsalProvider] Post-login return path handling failed:', storageError);
            }
          }
        } catch (redirectError) {
          log.error('[MsalProvider] MSAL or app auth bootstrap failed:', redirectError);
        }

        if (shouldMarkInitialized) {
          setMsalReady(true);
        }
      });
    }
  }, [isClient, msalInstance, setMsalInstance, setIsClient, ensureAppAuthReady]);

  // Wait for both client-side mount and config
  if (!isClient || !systemConfig) {
    return <LoadingFallback />;
  }

  // Wait for MSAL init and app JWT (jwt/app) before any data API calls from child routes.
  if (!msalReady || !appAuthReady || !msalInstance) {
    return <LoadingFallback />;
  }

  return <DefaultMsalProvider instance={msalInstance}>{children}</DefaultMsalProvider>;
}
